package com.example.histoire.web

import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.js.unsafeCast
import kotlin.math.pow
import kotlinx.browser.document
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.ImageData

/**
 * Luminance relative WCAG (0 = noir, 1 = blanc), moyenne sur l’image redimensionnée.
 * Utile pour choisir texte clair vs foncé selon la photo de fond.
 */
suspend fun averageImageDataUrlLuminance(dataUrl: String): Double =
    suspendCoroutine { cont ->
        if (!dataUrl.startsWith("data:image/")) {
            cont.resume(0.35)
            return@suspendCoroutine
        }
        val img = document.createElement("img").unsafeCast<HTMLImageElement>()
        var finished = false
        fun finishOnce(value: Double) {
            if (finished) return
            finished = true
            cont.resume(value)
        }
        img.addEventListener(
            "load",
            {
                try {
                    val canvas = document.createElement("canvas").unsafeCast<HTMLCanvasElement>()
                    val w = 48
                    val h = 48
                    canvas.width = w
                    canvas.height = h
                    val ctx = canvas.getContext("2d").unsafeCast<CanvasRenderingContext2D>()
                    ctx.drawImage(img, 0.0, 0.0, w.toDouble(), h.toDouble())
                    val id: ImageData = ctx.getImageData(0.0, 0.0, w.toDouble(), h.toDouble())
                    val raw = id.data.unsafeCast<dynamic>()
                    val len = raw.length as Int
                    var sum = 0.0
                    var n = 0
                    var i = 0
                    while (i < len) {
                        val r = (raw[i] as Number).toInt() and 0xFF
                        val g = (raw[i + 1] as Number).toInt() and 0xFF
                        val b = (raw[i + 2] as Number).toInt() and 0xFF
                        sum += relativeSrgbLuminance(r, g, b)
                        n++
                        i += 4
                    }
                    finishOnce(if (n > 0) sum / n else 0.35)
                } catch (_: Throwable) {
                    finishOnce(0.35)
                }
            },
        )
        img.addEventListener(
            "error",
            {
                finishOnce(0.35)
            },
        )
        img.src = dataUrl
    }

private fun linearChannel(c: Int): Double {
    val cs = (c and 0xFF) / 255.0
    return if (cs <= 0.03928) cs / 12.92 else ((cs + 0.055) / 1.055).pow(2.4)
}

private fun relativeSrgbLuminance(r: Int, g: Int, b: Int): Double {
    val R = linearChannel(r)
    val G = linearChannel(g)
    val B = linearChannel(b)
    return 0.2126 * R + 0.7152 * G + 0.0722 * B
}
